import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { adminLogin } from '../../api/adminClient';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { COLORS } from '../../constants/theme';

/**
 * Admin login page for the Chancellery backoffice (SF-112). Route `/chancellery/login`, outside
 * `RequireAdminAuth`. On success, stores the token via `useAdminAuth` (in-memory only) and
 * navigates to `/chancellery`.
 */
export function AdminLogin() {
  const navigate = useNavigate();
  const { setSession } = useAdminAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const { data, error: loginError } = await adminLogin(username, password);

    if (data) {
      setSession(data.accessToken, data.username);
      navigate('/chancellery', { replace: true });
    } else {
      setError(loginError ?? 'Login failed');
    }

    setIsLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: COLORS.bg }}
    >
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1
            className="text-center text-3xl font-bold tracking-widest"
            style={{ color: COLORS.text.primary }}
          >
            CHANCELLERY
          </h1>
          <h2
            className="mt-6 text-center text-2xl font-semibold"
            style={{ color: COLORS.text.secondary }}
          >
            Operator Login
          </h2>
        </div>

        <form
          className="mt-8 space-y-6 p-8 rounded-lg"
          style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
          onSubmit={handleSubmit}
        >
          <div className="space-y-4">
            <Input
              label="Username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="operator"
              autoComplete="username"
              required
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <div
              className="px-3 py-2 rounded text-sm"
              style={{ backgroundColor: COLORS.cardAlt, color: COLORS.negative, border: `1px solid ${COLORS.border}` }}
              role="alert"
            >
              {error}
            </div>
          )}

          <Button type="submit" isLoading={isLoading} className="w-full">
            Sign In
          </Button>
        </form>
      </div>
    </div>
  );
}
